
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { MenuCategory } from "@/data/dashboardData";
import { getIcon } from "@/utils/iconMapping";

interface QuickActionsTabProps {
  menuItems: MenuCategory[];
}

const QuickActionsTab: React.FC<QuickActionsTabProps> = ({ menuItems }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const topActions = menuItems.flatMap(category => 
    category.items.slice(0, 2)).slice(0, 6);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {topActions.map((item, idx) => {
        const IconComponent = getIcon(item.icon);
        return (
          <div 
            key={idx} 
            className="group bg-white/[0.06] border border-white/[0.10] rounded-xl p-5 hover:border-primary/30 hover:bg-white/[0.12] cursor-pointer transition-all duration-300 relative overflow-hidden" 
            onClick={() => navigate(item.path)}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/0 group-hover:from-primary/5 group-hover:to-accent/10 transition-all duration-300" />
            
            <div className="relative flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-all duration-300">
                  <IconComponent className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="ml-3 font-medium text-white">
                  {item.title[i18n.language as keyof typeof item.title]}
                </h3>
              </div>
              <ChevronRight className="h-5 w-5 text-white/30 group-hover:text-primary-foreground group-hover:translate-x-1 transition-all duration-300" />
            </div>
            <p className="relative mt-3 text-sm text-white/50 line-clamp-2">
              {item.description[i18n.language as keyof typeof item.description]}
            </p>
            {item.badge && (
              <span className="relative mt-3 inline-block px-2.5 py-1 text-xs font-semibold bg-primary/25 text-primary-foreground rounded-full">
                {item.badge}
              </span>
            )}
          </div>
        );
      })}
      
      <div 
        className="group bg-gradient-to-br from-primary/10 to-primary/20 border border-primary/15 rounded-xl p-5 cursor-pointer flex items-center justify-center hover:from-primary/15 hover:to-primary/30 hover:border-primary/30 transition-all duration-300"
        onClick={() => navigate("/admin/events")}
      >
        <div className="text-center">
          <p className="font-semibold text-primary-foreground">
            {isRTL ? "عرض جميع الإجراءات" : "View All Actions"}
          </p>
          <p className="text-sm text-white/50 mt-1">
            {isRTL ? "استكشف جميع الميزات المتاحة" : "Explore all available features"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuickActionsTab;
